import { type RefObject, useCallback, useEffect, useRef, useState } from 'react';

interface IOptions {
  /** Space (px) to keep between the container's bottom edge and the viewport bottom. */
  bottomGap?: number;
  /** Never bound the container below this height (px). */
  minHeight?: number;
}

interface IFillViewportHeight {
  /** Callback ref to attach to the scroll container. */
  setRef: (node: HTMLElement | null) => void;
  /** Live reference to the container node, for imperative reads (e.g. scroll-to-top). */
  containerRef: RefObject<HTMLElement | null>;
  /** Computed max-height (px), or `undefined` before the first measurement. */
  maxHeight: number | undefined;
}

/**
 * Bounds a scroll container's height to the space remaining below its top edge in
 * the viewport, so the container overflows-and-scrolls internally instead of
 * growing to fit all of its content.
 *
 * The authenticated app shell isn't height-bounded, so `flex-1` / `overflow: auto`
 * containers collapse to content height and never actually scroll — which means
 * scroll-event-driven infinite scroll never fires (GOTU-4127). Measuring the
 * container's real top offset (rather than a hardcoded `calc(100vh - Npx)` guess)
 * keeps the fix robust to the variable chrome above it (nav bar, header, tabs,
 * toolbar) and self-correcting on resize.
 *
 * Uses a callback ref so measurement runs exactly when the node mounts, even when
 * the container is rendered conditionally (e.g. behind a tab).
 */
export const useFillViewportHeight = ({
  bottomGap = 24,
  minHeight = 100,
}: IOptions = {}): IFillViewportHeight => {
  const containerRef = useRef<HTMLElement | null>(null);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);

  const recompute = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;

    const { top } = el.getBoundingClientRect();
    setMaxHeight(Math.max(minHeight, window.innerHeight - top - bottomGap));
  }, [bottomGap, minHeight]);

  const setRef = useCallback(
    (node: HTMLElement | null) => {
      containerRef.current = node;
      if (node) recompute();
    },
    [recompute],
  );

  useEffect(() => {
    window.addEventListener('resize', recompute);
    return () => window.removeEventListener('resize', recompute);
  }, [recompute]);

  return { setRef, containerRef, maxHeight };
};
